import { ReactNode } from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// SegmentedControl — pill-row toggle between 2-N mutually-exclusive options.
//
// Active segment renders with white surface + shadow on a slate-100 track;
// inactive segments are muted slate. Optional per-segment badge for unread
// counts (matches ResultsView's "With Response · N" pattern). Replaces 4
// hand-rolled toggle rows across the codebase: ResultsView response filter,
// EmailRail All/Unread, PollControlsBar Auto/Manual, HubShell Agentic/Classic.
//
// Sizes:
//   sm — h-7  (compact, dense toolbars)
//   md — h-8  (default, comfortable touch target)
//
// Per foundations.md §7 dark-mode pairs throughout.
// ─────────────────────────────────────────────────────────────────────────────

export interface SegmentedControlOption<TValue extends string = string> {
    value: TValue
    label: ReactNode
    /** Optional badge — typically an unread count. Renders as a red dot pill at the top-right corner. */
    badge?: ReactNode
    /** When true, the option renders disabled (not clickable + opacity-50). */
    disabled?: boolean
    /** Optional title on the segment for tooltip-on-hover (native title attr). */
    title?: string
}

export interface SegmentedControlProps<TValue extends string = string> {
    value: TValue
    options: SegmentedControlOption<TValue>[]
    onChange: (next: TValue) => void
    size?: 'sm' | 'md'
    /** Optional aria-label for the radiogroup. */
    'aria-label'?: string
    className?: string
    /** Stretch the track to fill its parent's width and distribute segments evenly.
     *  Default keeps the existing inline-flex behavior so callers that want a compact
     *  pill are unaffected. */
    fullWidth?: boolean
}

const sizeClasses: Record<NonNullable<SegmentedControlProps['size']>, { track: string; segment: string }> = {
    sm: { track: 'h-7 p-0.5 rounded-lg', segment: 'h-6 px-2.5 text-xs rounded-md' },
    md: { track: 'h-8 p-0.5 rounded-lg', segment: 'h-7 px-3 text-sm rounded-md' },
}

export function SegmentedControl<TValue extends string = string>({
    value,
    options,
    onChange,
    size = 'md',
    'aria-label': ariaLabel,
    className = '',
    fullWidth = false,
}: SegmentedControlProps<TValue>) {
    const tokens = sizeClasses[size]
    return (
        <div
            role="radiogroup"
            aria-label={ariaLabel}
            className={`${fullWidth ? 'flex w-full' : 'inline-flex'} items-center bg-slate-100 dark:bg-slate-800 ${tokens.track} ${className}`}
        >
            {options.map((opt) => {
                const isActive = opt.value === value
                return (
                    <div key={opt.value} className={`relative ${fullWidth ? 'flex-1' : ''}`}>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={isActive}
                            disabled={opt.disabled}
                            onClick={() => !opt.disabled && onChange(opt.value)}
                            title={opt.title}
                            className={
                                `${tokens.segment} ${fullWidth ? 'w-full' : ''} inline-flex items-center justify-center gap-1.5 font-medium transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ` +
                                (isActive
                                    ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100')
                            }
                        >
                            {opt.label}
                            {opt.badge != null && (
                                // Inline badge — sits next to the label inside the segment so it
                                // never overlaps the neighboring segment's active surface. Used to
                                // ride absolutely at -top-0.5 -right-1 which clipped into the next
                                // tab when the control is full-width.
                                <span className="inline-flex items-center justify-center min-w-[14px] h-[14px] rounded-full bg-red-500 text-white text-[9px] font-bold px-1">
                                    {opt.badge}
                                </span>
                            )}
                        </button>
                    </div>
                )
            })}
        </div>
    )
}
